import { useState, useEffect, useCallback, useRef } from 'react';
import { s3Service } from '../services/s3Service';

const POWER_BREAK_DURATION = 180; // 180 seconds

/**
 * Custom hook for Power Break timer management
 * Manages 180-second countdown timer with S3 synchronization
 * @returns Power Break state and control functions
 */
export function usePowerBreak() {
  const [isActive, setIsActive] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [startedAt, setStartedAt] = useState<Date | null>(null);
  const intervalRef = useRef<number | null>(null);

  /**
   * Calculate remaining time from startedAt timestamp
   */
  const calculateTimeRemaining = useCallback((startTime: Date): number => {
    const now = new Date();
    const elapsed = Math.floor((now.getTime() - startTime.getTime()) / 1000);
    const remaining = Math.max(0, POWER_BREAK_DURATION - elapsed);
    return remaining;
  }, []);

  /**
   * Start Power Break timer and sync to S3
   */
  const start = useCallback(async () => {
    const now = new Date();
    setIsActive(true);
    setStartedAt(now);
    setTimeRemaining(POWER_BREAK_DURATION);

    // Sync to S3
    try {
      await s3Service.updateState({
        powerBreak: {
          isActive: true,
          startedAt: now.toISOString(),
          duration: POWER_BREAK_DURATION,
        },
      });
    } catch (error) {
      console.error('Failed to sync Power Break start to S3:', error);
      // Continue with local state even if S3 sync fails
    }
  }, []);

  /**
   * Cancel Power Break timer and sync to S3
   */
  const cancel = useCallback(async () => {
    setIsActive(false);
    setStartedAt(null);
    setTimeRemaining(0);

    // Clear interval if running
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Sync to S3
    try {
      await s3Service.updateState({
        powerBreak: {
          isActive: false,
          startedAt: null,
          duration: POWER_BREAK_DURATION,
        },
      });
    } catch (error) {
      console.error('Failed to sync Power Break cancel to S3:', error);
      // Continue with local state even if S3 sync fails
    }
  }, []);

  /**
   * Load initial state from S3 on mount
   */
  useEffect(() => {
    const loadInitialState = async () => {
      try {
        const state = await s3Service.getState();
        
        if (state.powerBreak.isActive && state.powerBreak.startedAt) {
          const startTime = new Date(state.powerBreak.startedAt);
          const remaining = calculateTimeRemaining(startTime);
          
          // Only activate if there's time remaining
          if (remaining > 0) {
            setIsActive(true);
            setStartedAt(startTime);
            setTimeRemaining(remaining);
          } else {
            // Timer expired, cancel it
            await cancel();
          }
        }
      } catch (error) {
        console.error('Failed to load Power Break state from S3:', error);
        // Continue with default state
      }
    };

    loadInitialState();
  }, [calculateTimeRemaining, cancel]);

  /**
   * Update timer every second when active
   */
  useEffect(() => {
    if (isActive && startedAt) {
      // Clear any existing interval
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
      }

      // Update immediately
      const remaining = calculateTimeRemaining(startedAt);
      setTimeRemaining(remaining);

      // Set up interval to update every second
      intervalRef.current = window.setInterval(() => {
        const newRemaining = calculateTimeRemaining(startedAt);
        setTimeRemaining(newRemaining);

        // Auto-cancel when timer reaches zero
        if (newRemaining <= 0) {
          cancel();
        }
      }, 1000);

      // Cleanup on unmount or when dependencies change
      return () => {
        if (intervalRef.current !== null) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    }
    
    // Return undefined explicitly when not active
    return undefined;
  }, [isActive, startedAt, calculateTimeRemaining, cancel]);

  return {
    isActive,
    timeRemaining,
    start,
    cancel,
  };
}
